import {
  type ImageDetails,
  ImageReplaceForm,
  type ImageReplaceFormProps,
  type ImageReplaceFormValues,
} from '@carboncopyco/editor-ui';
import { addFileServiceDomainToImageVariables } from '@carboncopyco/editor-utils';
import {
  IFindAndReplaceRuleType,
  type IImageReplaceRule,
  type IImageReplaceWithVariableRule,
  type ImageVariable,
} from '@carboncopyco/types';
import { useMemo } from 'react';
import {
  useEditor,
  useEditorVariables,
} from '../../../context/EditorSdkProvider';
import type { IFindAndReplaceRuleState } from '../types';

export type EditControlsProps<
  T extends IImageReplaceRule | IImageReplaceWithVariableRule,
> = Pick<IFindAndReplaceRuleState<T>, 'onSubmit' | 'rule' | 'onCancel'> &
  Pick<ImageReplaceFormProps, 'autoFocus' | 'onFocus'>;

export const EditControls = <
  T extends IImageReplaceRule | IImageReplaceWithVariableRule,
>({
  onSubmit,
  rule,
  onCancel,
  autoFocus,
  onFocus,
}: EditControlsProps<T>) => {
  const editorSdk = useEditor();
  const { variables } = useEditorVariables();
  const imageVariables = useMemo(
    () =>
      addFileServiceDomainToImageVariables({
        variables,
        filesServiceDomain: editorSdk.filesServiceDomain,
      }),
    [variables, editorSdk.filesServiceDomain]
  );
  const updateImageReplaceRule = async (destinationImage: ImageDetails) => {
    const { link_to_file: destinationFileServiceLink } =
      await editorSdk.uploadImage(destinationImage.url);

    await onSubmit({
      ...rule,
      destinationFileServiceLink,
      destinationFileMetadata: destinationImage.metadata,
    });
  };

  const updateImageReplaceWithVariableRule = async (
    destinationVariable: ImageVariable
  ) => {
    await onSubmit({
      ...rule,
      destinationVariableId: destinationVariable.variableId,
    });
  };

  const updateRule = async ({
    destinationType,
    destination,
  }: ImageReplaceFormValues) => {
    const updateHandler = {
      image: updateImageReplaceRule,
      variable: updateImageReplaceWithVariableRule,
    }[destinationType] as (
      destination: ImageDetails | ImageVariable
    ) => Promise<void>;

    await updateHandler(destination);
  };

  const commonProps = {
    submitText: 'Save',
    onCancel,
    autoFocus,
    onFocus,
  };

  if (rule.type === IFindAndReplaceRuleType.replaceImage) {
    return (
      <ImageReplaceForm
        {...commonProps}
        initialValues={{
          sourceImage: {
            url: rule.sourceFileServiceLink,
            metadata: rule.sourceFileMetadata,
          },
          destinationType: 'image',
          destination: {
            url: rule.destinationFileServiceLink,
            metadata: rule.destinationFileMetadata,
          },
        }}
        onSubmit={updateRule}
      />
    );
  }

  if (rule.type === IFindAndReplaceRuleType.replaceImageWithVariable) {
    return (
      <ImageReplaceForm
        {...commonProps}
        initialValues={{
          sourceImage: {
            url: rule.sourceFileServiceLink,
            metadata: rule.sourceFileMetadata,
          },
          destinationType: 'variable',
          destination: imageVariables.find(
            ({ variableId }) => rule.destinationVariableId === variableId
          ),
        }}
        onSubmit={updateRule}
        variables={imageVariables}
      />
    );
  }

  return null;
};
